
import React from "react";
import { Instagram, AlertCircle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import IntegrationCard from "../common/IntegrationCard";
import InstagramConnectionForm from "./InstagramConnectionForm";
import InstagramSettingsForm from "./InstagramSettingsForm";
import { useInstagramIntegration } from "./hooks/useInstagramIntegration";
import { InstagramIntegrationProps } from "./types";

const InstagramIntegration: React.FC<InstagramIntegrationProps> = ({
  aiStaffId = "1",
  aiStaffName = "Your AI Assistant"
}) => {
  const {
    isConnected,
    connectionError,
    isLoading,
    isChecking,
    username,
    setUsername,
    isContentSchedulingEnabled,
    isAutoResponsesEnabled,
    handleConnect,
    handleDisconnect,
    handleContentSchedulingChange,
    handleAutoResponsesChange
  } = useInstagramIntegration(aiStaffId);

  return (
    <IntegrationCard
      title="Instagram Integration"
      description={`Connect ${aiStaffName} to your Instagram account to create and schedule posts`}
      icon={<Instagram className="h-5 w-5 text-pink-500" />}
      isConnected={isConnected}
      isChecking={isChecking}
      connectionError={connectionError}
    >
      {connectionError && (
        <Alert className="mb-4 bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">
            {connectionError}
          </AlertDescription>
        </Alert>
      )}

      {!isConnected ? (
        <InstagramConnectionForm
          username={username}
          setUsername={setUsername}
          isLoading={isLoading}
          onConnect={handleConnect}
        />
      ) : (
        <InstagramSettingsForm
          username={username}
          isContentSchedulingEnabled={isContentSchedulingEnabled}
          onContentSchedulingChange={handleContentSchedulingChange}
          isAutoResponsesEnabled={isAutoResponsesEnabled}
          onAutoResponsesChange={handleAutoResponsesChange}
          onDisconnect={async () => {
            await handleDisconnect();
            return void 0;
          }}
          aiStaffName={aiStaffName}
        />
      )}
    </IntegrationCard>
  );
};

export default InstagramIntegration;
